import { Alert, CircularProgress, Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/if"
import { Repeat } from "lib/@components/repeat"
import { ListItemBox } from "lib/@components/ListItemBox"
import { ResolutionStatusChip } from "routes/schedule/plugins/legislation-resolutions/resolution-status-chip"
import { ReviewProjectResolutionBulkActions } from "routes/plugins/review-projects/components/review-project-resolution-bulk-actions"
import { getProjectBridgeProcessingAlertMessage } from "./project-bridge-detail-presenters"
import { ProjectBridgeRewriteCard } from "./project-bridge-rewrite-card"
import { ProjectBridgeStatCard } from "./project-bridge-stat-card"

export function ProjectBridgeResolutionContent() {
    const {
        target: resolution,
        onDecideAll,
        onDecisionChange,
        onTextChange,
        onCriticalityChange,
        onCanonicalPropagationAction,
    } = useBoundContext()
    const changes = resolution?.proposedChanges ?? []
    const rewriteSections = resolution?.rewriteSections ?? []
    const undecidedCount = changes.filter((change) => change?.decision == null).length
    const unresolvedSectionCount = Number(resolution?.unresolvedSectionCount || 0)
    const acceptedCount = changes.filter((change) => change?.decision === "accept").length
    const noChangesReady =
        rewriteSections.every(function withoutRewrite(section) {
            return !section?.hasRewrite
        }) && resolution?.status === "complete"
    const tasksAffectedPercent = getRatioPercent(resolution?.tasksAffected, resolution?.totalTasks)
    const readySectionsPercent = getRatioPercent(resolution?.readySectionCount, resolution?.sectionCount)
    const processingMessage = getProjectBridgeProcessingAlertMessage(resolution)

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5">
                    {resolution?.code} — {resolution?.title}
                </Typography>
                <ResolutionStatusChip status={resolution?.status} />
            </Stack>

            <ListItemBox spacing={1} sx={{ flexWrap: "wrap", justifyContent: "stretch", alignItems: "stretch" }}>
                <ProjectBridgeStatCard
                    title="Overall Match"
                    total={`${Number(resolution?.overallMatchPercent || 0)}%`}
                    percent={Number(resolution?.overallMatchPercent || 0)}
                    color="info"
                />
                <ProjectBridgeStatCard
                    title="Tasks Affected"
                    total={`${Number(resolution?.tasksAffected || 0)} / ${Number(resolution?.totalTasks || 0)}`}
                    percent={tasksAffectedPercent}
                    color="primary"
                />
                <ProjectBridgeStatCard
                    title="Strongest Overlap"
                    total={`${Number(resolution?.longestOverlap || 0)} words`}
                    color="warning"
                />
                <ProjectBridgeStatCard
                    title="Ready Sections"
                    total={`${Number(resolution?.readySectionCount || 0)} / ${Number(resolution?.sectionCount || 0)}`}
                    percent={readySectionsPercent}
                    color="success"
                />
            </ListItemBox>

            <If truthy={resolution?.errorMessage}>
                <Alert severity="error">{resolution?.errorMessage}</Alert>
            </If>

            <If truthy={processingMessage}>
                <Alert severity="info" icon={<CircularProgress size={18} color="inherit" thickness={5} />}>
                    {processingMessage}
                </Alert>
            </If>

            <If truthy={unresolvedSectionCount > 0}>
                <Alert severity="warning">
                    {unresolvedSectionCount} section{unresolvedSectionCount !== 1 ? "s" : ""} cannot be generated yet
                    because the schedule or task mapping could not be resolved automatically.
                </Alert>
            </If>

            <If truthy={noChangesReady}>
                <Alert severity="success">
                    No AI rewrite proposals are currently stored for this schedule in the latest Project Bridge run.
                </Alert>
            </If>

            <If truthy={undecidedCount > 0}>
                <Alert severity="warning">
                    {undecidedCount} rewrite{undecidedCount !== 1 ? "s" : ""} still need a decision.
                </Alert>
            </If>

            <If truthy={changes.length > 0}>
                <ReviewProjectResolutionBulkActions changes={changes} onDecideAll={onDecideAll} />
            </If>

            <If truthy={acceptedCount > 0}>
                <Typography variant="body2" color="text.secondary">
                    {acceptedCount} rewrite{acceptedCount !== 1 ? "s" : ""} currently marked as accepted for authoring.
                </Typography>
            </If>

            <Repeat
                list={rewriteSections}
                item={
                    <ProjectBridgeRewriteCard
                        onDecisionChange={onDecisionChange}
                        onTextChange={onTextChange}
                        onCriticalityChange={onCriticalityChange}
                        onCanonicalPropagationAction={onCanonicalPropagationAction}
                    />
                }
                keyFn={keyBySection}
            />
        </Stack>
    )

    function keyBySection(section) {
        return section?.key || section?.changeId
    }

    function getRatioPercent(numerator, denominator) {
        const denominatorNumber = Number(denominator || 0)
        if (!denominatorNumber) return 0
        return Math.round((Number(numerator || 0) / denominatorNumber) * 100)
    }
}
